import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import { EstablishmentAPIPromiseClient } from "../gen/witbooking/backoffice/property/v1/establishment_api_grpc_web_pb";
import { GetPropertyRequest } from "../gen/witbooking/backoffice/property/v1/establishment_api_pb";
import { PropertyAPIPromiseClient } from "../gen/witbooking/backoffice/property/v1/property_api_grpc_web_pb";
import { GetPropertyConfigurationRequest } from "../gen/witbooking/backoffice/property/v1/property_api_pb";
import { ACCESS_DENIED } from "../lib/auth";
import { useGrpcClient, usePropertyOptionalGrpcCall } from "../lib/grpc";

export type PropertyConfigurationValue = { [key: string]: string } | undefined | null;

function isValidIanaTimeZone(timeZone: string | undefined): timeZone is string {
  if (!timeZone) {
    return false;
  }
  try {
    // Throws a RangeError for an unrecognizable/malformed IANA time zone identifier.
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch (error) {
    return false;
  }
}

export const PropertyConfigurationContext = createContext<PropertyConfigurationValue>(undefined);

const CONFIG_KEYS = [
  "showServicesManager",
  "numberOfPromotionsGroup",
  "showEmailTemplateManager",
  "showAdvancedEmailsManager",
  "showNewReservationsList",
  "showBatchCancellation",
  "showPackagesManager",
  "showAccommodationsManager",
  "enableAutomaticCharges",
  "showBatchInventories",
  "showNewAriManagement",
  "showInventoryTable",
  "showHistoricalReservations",
  "useAriService",
  "taxesText",
  "gtmCodes",
  "propertyImageLimit",
  "showAltARIManagerStyles",
  "numberOfDaysARIManager",
  "showGroupBookingModule",
  "allowCustomerCommentEdit",
  "onlineStoreActive",
  "onlineStoreMode",
  "onlineStoreCustomDomain",
  "occupationType",
  "isQuotationActive",
  "useChildEngineCancelPolicyOrderInsteadAgr",
  "isOrchestratorActive",
  "isOrchestratorTestAbActive",
  "isWhatsAppConciergeActive",
  "b2bChannelEnabled",
  "b2bMasterModeEnabled",
  // Only the Maarlab master switch is exposed app-wide. The Maarlab UPSELLING API key and the
  // environment key are deliberately excluded — the key must never reach the browser outside the
  // system_superadmin-only Configuration Manager. See US-001 BR12.
  "isMaarlabActive",
  "extrasCategoriesActive",
];

export function PropertyConfigurationProvider({ children }: PropsWithChildren<{}>) {
  const [propertyConfig, setPropertyConfig] = useState<{ [key: string]: string } | undefined>();
  const propertyGrpcClient = useGrpcClient(PropertyAPIPromiseClient);
  const establishmentGrpcClient = useGrpcClient(EstablishmentAPIPromiseClient);

  const getPropertyConfiguration = usePropertyOptionalGrpcCall(
    propertyGrpcClient,
    propertyGrpcClient.getPropertyConfiguration
  );
  const getEstablishmentConfiguration = usePropertyOptionalGrpcCall(
    establishmentGrpcClient,
    establishmentGrpcClient.getProperty
  );

  useEffect(() => {
    async function load() {
      try {
        if (!getPropertyConfiguration || !getEstablishmentConfiguration) {
          return;
        }
        const requestPropertyConfig = new GetPropertyConfigurationRequest();
        const requestEstablishmentConfig = new GetPropertyRequest();

        CONFIG_KEYS.forEach(key => requestPropertyConfig.addConfigurationKeys(key));

        const [responsePropertyConfiguration, responseEstablishmentConfig] = await Promise.all([
          getPropertyConfiguration(requestPropertyConfig),
          getEstablishmentConfiguration(requestEstablishmentConfig),
        ]);

        const propertyConfiguration = responsePropertyConfiguration
          .toObject()
          .configurationsMap.reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {} as { [key: string]: string });

        const propertyObject = responseEstablishmentConfig.getProperty()?.toObject();

        const timeZone = propertyObject?.location?.timeZone;

        const translations = propertyObject?.translationsList ?? [];
        const propertyName = (translations.find(t => t.locale === "es") ?? translations[0])?.name;

        const isTimeZoneValid = isValidIanaTimeZone(timeZone);

        if (timeZone && !isTimeZoneValid) {
          console.error("Invalid or unrecognizable property time zone received", timeZone);
        }

        setPropertyConfig({
          ...propertyConfiguration,
          ...(isTimeZoneValid && { timeZone }),
          ...(propertyName && { propertyName }),
        });
      } catch (error) {
        if (error === ACCESS_DENIED) {
          console.error("Access denied", error);
        } else {
          console.error("Error retrieving rpt", error);
        }
        setPropertyConfig({});
      }
    }

    load();
  }, [getPropertyConfiguration, getEstablishmentConfiguration]);

  return (
    <PropertyConfigurationContext.Provider value={propertyConfig}>
      {children}
    </PropertyConfigurationContext.Provider>
  );
}

export function usePropertyConfiguration(): PropertyConfigurationValue {
  return useContext(PropertyConfigurationContext);
}

//This one is for compatibility with witadmin
export function withPropertyConfiguration<T>(Component: React.ComponentType<T>) {
  return function WrappedPropertyConfigComponent(props: T) {
    return (
      <PropertyConfigurationContext.Consumer>
        {(value: PropertyConfigurationValue) => <Component {...props} propertyConfig={value} />}
      </PropertyConfigurationContext.Consumer>
    );
  };
}
